import * as React from 'react';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// MUI Components
import { useTheme } from '@mui/material/styles';
import { Box, Paper, IconButton, TextField, Chip, Checkbox } from '@mui/material';
import { Table, TableHead, TableBody, TableCell, TableContainer, TableFooter, TablePagination, TableRow } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';

// MUI Icons
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import DetailsIcon from '@mui/icons-material/Details';
import ClearIcon from '@mui/icons-material/Clear';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

// API Queries
import { useGetFilteredComplaintsQuery, useLazyGetFilteredComplaintsLazyQuery } from '../../features/complaints/complaintsApiSlice';
import { useGetUsersQuery } from '../../features/users/usersApiSlice';
import { useGetSupportUsersQuery } from '../../features/supportusers/supportusersApiSlice';
import { useGetComplMastersQuery } from '../../features/complmaster/complmasterApiSlice';
import { useGetStoreOfficesQuery } from '../../features/storeoffices/storeofficesApiSlice';

// Styles
import { TableContStyle, TableHeadStyle } from '../../Styles/TableStyles';
import styled from 'styled-components';

// Utilities
import ExcelDownload from '../../data/exceldownload';
import dayjs from 'dayjs';
import moment from 'moment';
import nameStringFn from '../../data/nameStringFn';
import ComplaintActionOptions from '../../data/ComplaintActionOptions';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const Button2Div = styled.div`
  display: flex;
  justify-content: end;
`;

const Wrapper = styled.div`
  display: flex;
  background-color: white;
  align-items: center;
`;

const SpanContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-right: 5px;
`;

const StatusSpan = styled.span`
  font-size: 16px;
  font-family: Arial, sans-serif;
  font-weight: bold;
  color: #333;
`;

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const tableFontSize = 15;

// Debounce function
function debounce(func, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(this, args), delay);
  };
}

export default function UnivComplaintTable({
  keyArrayDwld,
  columnFilter,
  checkbox,
  selectedIds,
  setSelectedIds,
  asstCounter,
  setAsstCounter,
  setRowid,
  setViewDetail,
  filterProps,
  viewType = 'inbox'
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [downloadData, setDownloadData] = useState({});
  const [tableData, setTableData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [assetNameOptions, setAssetNameOptions] = useState([]);

  // Define filter states for complaints (updated to match new structure)
  const [filters, setFilters] = useState({
    complaintNo: "",
    assetId: "", // Keep for backend filtering
    assetName: [], // Now array for multi-select
    complaintCat: [],
    complaintSub: [],
    req_creator_id: [], // Updated from userId
    registeredby: "", // Changed back to string for name search
    curr_sent_to: [], // Updated from currentlyAssignedTo
    assignedto: "", // Changed back to string for name search
    status: [],
    curr_status: [], // Added new status field
    store: [],
    from_created_at: "",
    to_created_at: "",
    curr_sent_to_tag: [], // Added new tag field
    curr_sent_to_string: "", // Added new string field
    // Permanent filters updated to match new structure
    perm_complaintNo: [],
    perm_complaintCat: [],
    perm_complaintSub: [],
    perm_req_creator_id: [], // Updated from perm_userId
    perm_curr_sent_to: [], // Updated from perm_currentlyAssignedTo
    perm_store: [],
    perm_status: [],
    perm_curr_status: [], // Added new status permanent filter
    perm_assetId: [], // Keep for backend filtering
    perm_assetName: [], // Added permanent filter for assetName
    perm_curr_sent_to_tag: [], // Added new tag permanent filter
    perm_curr_sent_to_string: [], // Added new string permanent filter
    req_data_sent_to_tags: [], // Added for sentbox filtering
    flow_users: [] // Added for user flow tracking
  });

  // Regular paginated query for displaying data
  const { data: filteredData, isLoading, error } = useGetFilteredComplaintsQuery({
    page: page + 1,
    limit: rowsPerPage,
    filters: JSON.stringify(filters),
  });

  // Lazy query to fetch data only when triggered
  const [fetchAllData, { data: allData, isLoading: allDataLoading }] = useLazyGetFilteredComplaintsLazyQuery();

  // Get data for dropdowns - MOVED TO TOP
  const { users } = useGetUsersQuery("UserList", {
    selectFromResult: ({ data }) => ({
      users: data?.entities
    }),
  });

  const { supportusers } = useGetSupportUsersQuery("supportuser", {
    selectFromResult: ({ data }) => ({
      supportusers: data?.entities
    }),
  });

  const { complmaster } = useGetComplMastersQuery("complList", {
    selectFromResult: ({ data }) => ({
      complmaster: data ? data?.entities : []
    }),
  });

  const { storeoffices } = useGetStoreOfficesQuery("storeoffices", {
    selectFromResult: ({ data }) => ({
      storeoffices: data ? data?.entities : []
    }),
  });

  // Extract options for dropdowns
  const categOptions = complmaster ? Object.values(complmaster).map(x => x.Category) : [];
  const subjectOptions = complmaster ? 
    (filters.complaintCat.length ? 
      Object.values(complmaster).filter(x => filters.complaintCat.includes(x.Category)).map(x => x.Subject).flat() :
      Object.values(complmaster).map(x => x.Subject).flat()
    ) : [];
  const storeOptions = storeoffices ? Object.values(storeoffices).map(x => x.storeoffice) : [];
  const statusOptions = [...new Set(Object.values(ComplaintActionOptions).flat().map(x => x.updatedStatus))].sort();

  // Debug logging
  useEffect(() => {
    console.log("Current filters:", filters);
    console.log("Filtered data:", filteredData);
    console.log("Is loading:", isLoading);
    console.log("Error:", error);
  }, [filters, filteredData, isLoading, error]);

  // Fetch all data when filters change
  useEffect(() => {
    if (filters) {
      console.log("Fetching all data with filters:", filters);
      fetchAllData({
        page: 1,
        limit: 99999,
        filters: JSON.stringify(filters),
      });
    }
  }, [filters, fetchAllData]);

  // Apply filter props if provided
  useEffect(() => {
    if (filterProps) {
      console.log("Applying filter props:", filterProps);
      setFilters(prevFilters => {
        const resetProps = {
          perm_complaintNo: [],
          perm_complaintCat: [],
          perm_complaintSub: [],
          perm_req_creator_id: [],
          perm_curr_sent_to: [],
          perm_store: [],
          perm_status: [],
          perm_assetId: [],
          perm_assetName: [],
          perm_curr_sent_to_tag: [],
          perm_curr_sent_to_string: [],
          req_data_sent_to_tags: [],
          flow_users: []
        };
        
        const newFilters = { ...prevFilters, ...resetProps };
        
        for (let key in filterProps) {
          if (filterProps[key] !== undefined) {
            console.log(`Setting permanent filter ${key} to:`, filterProps[key]);
            newFilters[key] = filterProps[key];
          }
        }
        
        console.log("New filters after applying filterProps:", newFilters);
        return newFilters;
      });
    }
  }, [filterProps]);

  // Update table data when API data changes - backend filtering only
  useEffect(() => {
    if (filteredData) {
      let processedData = filteredData.complaints;
      console.log("Processing data:", processedData);
      console.log("Filtered data received:", {
        complaintsCount: filteredData.complaints?.length,
        totalFilteredComplaints: filteredData.totalFilteredComplaints,
        frontendFilters: filteredData.frontendFilters
      });
      
      // All filtering is now done on the backend
      console.log("Backend filtering complete. Final processed data:", processedData);
      setTableData(processedData);
    }
  }, [filteredData, users, supportusers]);

  // Get unique asset names for dropdown from all data, not just current page
  useEffect(() => {
    if (allData?.complaints) {
      const uniqueAssetNames = [...new Set(allData.complaints.map(x => x.assetName).filter(Boolean))];
      setAssetNameOptions(uniqueAssetNames);
    }
  }, [allData]);

  // Handle selection logic
  useEffect(() => {
    if (selectedIds?.length && tableData) {
      let assetNameCounter = {};
      for (let value of tableData.filter(x => selectedIds.includes(x._id)).map(x => x.assetName)) {
        if (assetNameCounter[value]) {
          assetNameCounter = { ...assetNameCounter, [value]: assetNameCounter[value] + 1 };
        } else {
          assetNameCounter = { ...assetNameCounter, [value]: 1 };
        }
      }
      setAsstCounter && setAsstCounter(assetNameCounter);
    }
  }, [selectedIds, tableData]);

  // Prepare download data when API data is available
  useEffect(() => {
    if (allData?.complaints && keyArrayDwld) {
      const tempDownloadData = structuredClone(allData.complaints || []);
      
      const finalDownloadData = tempDownloadData.map(({ _id, ...others }) => ({
        ...others,
        registered_by: others.req_creator_details || (users ? nameStringFn(users[others.req_creator_id]) : ''),
        assigned_to: others.curr_sent_to_string || (supportusers ? nameStringFn(supportusers[others.curr_sent_to]) : ''),
        filing_date: others.created_at ? 
          moment(others.created_at).format("DD/MM/YYYY HH:mm") : 
          (others.req_data && others.req_data.length > 0 ? 
            moment(others.req_data[0]?.time_stamp).format("DD/MM/YYYY HH:mm") : 
            (others.flow && others.flow.length > 0 ? 
              moment(others.flow[0]?.timeStamp).format("DD/MM/YYYY HH:mm") : 
              ''
            )
          ),
        current_status: others.curr_status || 
          (others.req_data && others.req_data.length > 0 ? 
            others.req_data[others.req_data.length - 1]?.action : 
            (others.flow && others.flow.length > 0 ? 
              others.flow[others.flow.length - 1]?.status : 
              ''
            )
          ),
        current_tag: others.curr_sent_to_tag || '',
        complaint_id: others.complaintNo || '',
      }));

      const finalSortedDownloadData = finalDownloadData.map((value) =>
        keyArrayDwld.reduce((acc, key) => ({ ...acc, [key]: value[key] }), {})
      );

      setDownloadData(finalSortedDownloadData);
    }
  }, [allData, keyArrayDwld, users, supportusers]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCheckboxChange = (event, id) => {
    if (event.target.checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds?.filter((selectedId) => selectedId !== id));
    }
  };

  const handleSelectAllChange = (event) => {
    const checked = event.target.checked;
    setSelectAll(checked);
    
    if (checked) {
      const allIds = tableData.map((row) => row._id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleViewDetail = (e, id) => {
    setRowid(id);
    setViewDetail(true);
  };

  const handleSearchChange = debounce((event, newValue, name) => {
    let value;

    if (name) {
      if (newValue?.$d) {
        // Properly format date for backend - fix date handling
        const date = newValue.toDate();
        value = date.toISOString();
        console.log(`Date converted: ${newValue} -> ${value}`);
      } else if (newValue === null || newValue === undefined) {
        // Handle clearing of date/autocomplete fields
        value = Array.isArray(filters[name]) ? [] : "";
      } else {
        value = newValue;
      }
    } else {
      name = event.target.name;
      value = event.target.value;
      
      // Don't convert registeredby and assignedto to arrays here - they should remain as strings
      // The backend will handle them as frontend filters for partial name matching
    }

    console.log(`Setting filter ${name} to:`, value);

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
    
    // Reset page when filter changes
    setPage(0);
  }, 500);

  // Show loading state
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
        <div>Loading complaints...</div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '20px', color: 'red' }}>
        <div>Error loading complaints: {error.message || 'Unknown error'}</div>
      </div>
    );
  }

  return (
    <>
      {keyArrayDwld && (
        <Button2Div>
          <ExcelDownload data={downloadData} filename={"complaintDetail"} sheetName={"sheet1"} />
        </Button2Div>
      )}
      
      <div>
        {checkbox && (
          <SpanContainer>
            <StatusSpan>
              {`Total Items Selected: ${selectedIds.length} ${
                selectedIds.length ? `(${Object.entries(asstCounter).map(x => ` ${x[0]}:${x[1]}`)})` : ""
              }`}
            </StatusSpan>
          </SpanContainer>
        )}
        
        <TableContainer component={Paper} sx={{ backgroundColor: TableContStyle.backgroundColor }}>
          <Table sx={{ minWidth: 500 }} aria-label="custom pagination table">
            <TableHead style={{ backgroundColor: TableHeadStyle.backgroundColor }}>
              <TableRow>
                {checkbox && (
                  <TableCell>
                    <Checkbox
                      name="select-all"
                      onChange={handleSelectAllChange}
                    />
                  </TableCell>
                )}
                <TableCell style={TableHeadStyle} sx={{ width: { md: "3%" } }}>
                  Sl.No.
                </TableCell>
                {columnFilter?.includes("ComplaintNo") && (
                  <TableCell style={TableHeadStyle} sx={{ width: { md: "10%" } }} align="left">
                    Complaint No.
                  </TableCell>
                )}
                {columnFilter?.includes("ComplaintFilingTime") && (
                  <TableCell style={TableHeadStyle} sx={{ width: { md: "12%" } }} align="left">
                    Register Time
                  </TableCell>
                )}
                {columnFilter?.includes("ComplaintCat") && (
                  <TableCell style={TableHeadStyle} sx={{ width: { md: "12%" } }} align="left">
                    Complaint Categ
                  </TableCell>
                )}
                {columnFilter?.includes("Subject") && (
                  <TableCell style={TableHeadStyle} sx={{ width: { md: "11%" } }} align="left">
                    Subject
                  </TableCell>
                )}
                {columnFilter?.includes("RegisteredBy") && (
                  <TableCell style={TableHeadStyle} sx={{ width: { md: "12%" } }} align="left">
                    Registered by
                  </TableCell>
                )}
                {columnFilter?.includes("AssignedTo") && (
                  <TableCell style={TableHeadStyle} sx={{ width: { md: "11%" } }} align="left">
                    Assigned To
                  </TableCell>
                )}
                {columnFilter?.includes("Status") && (
                  <TableCell style={TableHeadStyle} sx={{ width: { md: "11%" } }} align="left">
                    Status
                  </TableCell>
                )}
                {columnFilter?.includes("AssetName") && (
                  <TableCell style={TableHeadStyle} sx={{ width: { md: "11%" } }} align="left">
                    Asset Name
                  </TableCell>
                )}
                {columnFilter?.includes("Store") && (
                  <TableCell style={TableHeadStyle} sx={{ width: { md: "11%" } }} align="left">
                    Store
                  </TableCell>
                )}
                {columnFilter?.includes("ViewDetail") && (
                  <TableCell style={TableHeadStyle} sx={{ width: { md: "11%" } }} align="left">
                    Detail
                  </TableCell>
                )}
              </TableRow>

              <TableRow>
                {checkbox && <TableCell></TableCell>}
                <TableCell></TableCell>
                
                {columnFilter?.includes("ComplaintNo") && (
                  <TableCell>
                    <TextField
                      sx={{ background: "white" }}
                      name="complaintNo"
                      onChange={handleSearchChange}
                      label="Search Complaint No."
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                )}

                {columnFilter?.includes("ComplaintFilingTime") && (
                  <TableCell>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DemoContainer components={["DatePicker", "DatePicker"]} sx={{ margin: "0px", display: "flex" }}>
                        <Wrapper>
                          <DatePicker
                            label="From"
                            maxDate={filters?.to_created_at ? dayjs(filters.to_created_at) : null}
                            slotProps={{ textField: { size: "small" } }}
                            sx={{ backgroundColor: "white" }}
                            value={filters?.from_created_at ? dayjs(filters.from_created_at) : null}
                            format="DD/MM/YYYY"
                            onChange={(newValue) => {
                              console.log("From date picker changed:", newValue);
                              handleSearchChange(null, newValue, "from_created_at");
                            }}
                          />
                          {filters?.from_created_at && (
                            <ClearIcon onClick={() => {
                              console.log("Clearing from date");
                              handleSearchChange(null, null, "from_created_at");
                            }} />
                          )}
                        </Wrapper>
                      </DemoContainer>
                    </LocalizationProvider>
                    {filters?.from_created_at && (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DemoContainer components={["DatePicker", "DatePicker"]}>
                          <Wrapper>
                            <DatePicker
                              label="To"
                              minDate={filters?.from_created_at ? dayjs(filters.from_created_at) : null}
                              slotProps={{ textField: { size: "small" } }}
                              sx={{ backgroundColor: "white" }}
                              value={filters?.to_created_at ? dayjs(filters.to_created_at) : null}
                              format="DD/MM/YYYY"
                              onChange={(newValue) => {
                                console.log("To date picker changed:", newValue);
                                handleSearchChange(null, newValue, "to_created_at");
                              }}
                            />
                            {filters?.to_created_at && (
                              <ClearIcon onClick={() => {
                                console.log("Clearing to date");
                                handleSearchChange(null, null, "to_created_at");
                              }} />
                            )}
                          </Wrapper>
                        </DemoContainer>
                      </LocalizationProvider>
                    )}
                  </TableCell>
                )}

                {columnFilter?.includes("ComplaintCat") && (
                  <TableCell>
                    <Autocomplete
                      multiple
                      onChange={(event, newValue) => handleSearchChange(event, newValue, "complaintCat")}
                      size="small"
                      value={filters?.complaintCat}
                      options={categOptions.sort()}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} style={{ fontSize: "2 rem" }}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 0, marginLeft: 0 }}
                            checked={selected}
                          />
                          {option}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Search Complaint Categ" sx={{ background: "white", width: "100%" }} />
                      )}
                    />
                  </TableCell>
                )}

                {columnFilter?.includes("Subject") && (
                  <TableCell>
                    <Autocomplete
                      multiple
                      onChange={(event, newValue) => handleSearchChange(event, newValue, "complaintSub")}
                      size="small"
                      value={filters?.complaintSub}
                      options={subjectOptions.sort()}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} style={{ fontSize: "2 rem" }}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 0, marginLeft: 0 }}
                            checked={selected}
                          />
                          {option}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Search Subject" sx={{ background: "white", width: "100%" }} />
                      )}
                    />
                  </TableCell>
                )}

                {columnFilter?.includes("RegisteredBy") && (
                  <TableCell>
                    <TextField
                      sx={{ background: "white" }}
                      name="registeredby"
                      onChange={handleSearchChange}
                      label="Search Complaint User"
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                )}

                {columnFilter?.includes("AssignedTo") && (
                  <TableCell>
                    <TextField
                      sx={{ background: "white" }}
                      name="assignedto"
                      onChange={handleSearchChange}
                      label="Support User"
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                )}

                {columnFilter?.includes("Status") && (
                  <TableCell>
                    <Autocomplete
                      multiple
                      onChange={(event, newValue) => handleSearchChange(event, newValue, "curr_status")}
                      size="small"
                      value={filters?.curr_status}
                      options={statusOptions}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} style={{ fontSize: "2 rem" }}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 0, marginLeft: 0 }}
                            checked={selected}
                          />
                          {option}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Search Status" sx={{ background: "white", width: "100%" }} />
                      )}
                    />
                  </TableCell>
                )}

                {columnFilter?.includes("AssetName") && (
                  <TableCell>
                    <Autocomplete
                      multiple
                      onChange={(event, newValue) => handleSearchChange(event, newValue, "assetName")}
                      size="small"
                      value={filters?.assetName}
                      options={assetNameOptions.sort()}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} style={{ fontSize: "2 rem" }}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 0, marginLeft: 0 }}
                            checked={selected}
                          />
                          {option}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Search Asset Name" sx={{ background: "white", width: "100%" }} />
                      )}
                    />
                  </TableCell>
                )}

                {columnFilter?.includes("Store") && (
                  <TableCell>
                    <Autocomplete
                      multiple
                      onChange={(event, newValue) => handleSearchChange(event, newValue, "store")}
                      size="small"
                      value={filters?.store}
                      options={storeOptions}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} style={{ fontSize: "2 rem" }}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 0, marginLeft: 0 }}
                            checked={selected}
                          />
                          {option}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Search Store" sx={{ background: "white", width: "100%" }} />
                      )}
                    />
                  </TableCell>
                )}

                {columnFilter?.includes("ViewDetail") && <TableCell></TableCell>}
              </TableRow>
            </TableHead>
            
            <TableBody>
              {tableData?.length > 0 &&
                tableData.slice(0, rowsPerPage).map((row, index) => {
                  const isUnread = viewType === 'inbox' && !row.opened;
                  const fontWeight = isUnread ? 'bold' : 'normal';
                  
                  return (
                    <TableRow key={index} sx={{ fontWeight }}>
                      {checkbox && (
                        <TableCell>
                          <Checkbox
                            name={`select-${row._id}`}
                            checked={selectedIds?.includes(row._id)}
                            onChange={(event) => handleCheckboxChange(event, row._id)}
                          />
                        </TableCell>
                      )}
                      
                      <TableCell component="th" scope="row" align="center" sx={{ fontSize: tableFontSize, fontWeight }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>

                      {columnFilter?.includes("ComplaintNo") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          {row?.complaintNo}
                        </TableCell>
                      )}
                      
                      {columnFilter?.includes("ComplaintFilingTime") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          {row?.created_at ? 
                            moment(row.created_at).format("DD-MM-YYYY, HH:mm") : 
                            (row?.req_data && row.req_data.length > 0 ? 
                              moment(row.req_data[0]?.time_stamp).format("DD-MM-YYYY, HH:mm") : 
                              (row?.flow && row.flow.length > 0 ? 
                                moment(row.flow[0]?.timeStamp).format("DD-MM-YYYY, HH:mm") : 
                                'N/A'
                              )
                            )
                          }
                        </TableCell>
                      )}
                      
                      {columnFilter?.includes("ComplaintCat") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          {row?.complaintCat}
                        </TableCell>
                      )}

                      {columnFilter?.includes("Subject") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          {row?.complaintSub}
                        </TableCell>
                      )}
                      
                      {columnFilter?.includes("RegisteredBy") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          {row?.req_creator_details || (users ? nameStringFn(users[row?.req_creator_id]) : "")}
                        </TableCell>
                      )}
                      
                      {columnFilter?.includes("AssignedTo") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          {row?.curr_sent_to_string || (supportusers ? nameStringFn(supportusers[row?.curr_sent_to]) : "")}
                        </TableCell>
                      )}
                      
                      {columnFilter?.includes("Status") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          {row?.curr_status || 
                           (row?.req_data && row.req_data.length > 0 ? 
                             row.req_data[row.req_data.length - 1]?.action : 
                             (row?.flow && row.flow.length > 0 ? 
                               row.flow[row.flow.length - 1]?.status : 
                               'N/A'
                             )
                           )
                          }
                        </TableCell>
                      )}
                      
                      {columnFilter?.includes("AssetName") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          {row?.assetName}
                        </TableCell>
                      )}
                      
                      {columnFilter?.includes("Store") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          {row?.store}
                        </TableCell>
                      )}
                      
                      {columnFilter?.includes("ViewDetail") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          <DetailsIcon
                            sx={{ color: "green", fontSize: "40px", "&:hover": { cursor: "pointer" } }}
                            onClick={(e) => handleViewDetail(e, row._id)}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
            </TableBody>
            
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 100, 500, 1000]}
                  colSpan={3}
                  count={filteredData?.totalFilteredComplaints || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { "aria-label": "rows per page" },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </div>   
    </>
  );
}